import { Injectable } from '@angular/core';
import { HttpService } from '../../services/http.service';
import { Licenses } from '../../../models/licenses';
import { ExternalImageSearchApis } from '../../../config/external-image-search-api.config';
import { IStandardizedImageSearchResults } from 'src/models/standardized-image-search-results';
import { IImageWrapper } from '../../../models/image-wrapper';

export interface IDuckduckgoImageSearchQuery {
  q: string;
  l?: string; // license
  page?: number;
  s: string; // source: DD is duckduckgo
}

/**
 * Parameter for a flickr query
 */
export interface IDuckduckgoImageSearchQueryOptions {
  license?: Licenses;
  /**
   * Targeted page of the results
   */
  page?: number;
}

export interface IDuckduckgoStep1Result {
  started: boolean;
  search_id: string;
}

export interface IDuckduckgoStep2Result {
  status: number;
  search_id?: string;
}

export interface IDuckduckgoPublicSearchImageItem {
  url: string;
  thumbnail_url: string;
  width: number;
  height: number;
  thumbnail_width: number;
  thumbnail_height: number;
  title: string;
  author: string;
  author_url: string;
  score: number;
  license: string;
  license_version: string;
  license_url: string;
} 

export interface IDuckduckgoImageSearchResult {
  count: number;
  next: string;
  previous: string;
  results: IDuckduckgoPublicSearchImageItem[];
}

@Injectable({
  providedIn: 'root'
})
/**
 * Abstracts communication with Duckduckgo API
 */
export class DuckduckgoImageSearchService {
  private _license: Licenses;


  /**
   * Makes use of the HTTPService
   * @param _http The http service
   */
   constructor(
    private _http: HttpService
  ) { }

  /**
   * Query the flickr API
   * @param query The actual query
   * @param options Some further options
   */
  public async query(query: string, options?: IDuckduckgoImageSearchQueryOptions): Promise<IDuckduckgoImageSearchResult> {
    const _queryObject: IDuckduckgoImageSearchQuery = {
      q: query,
      page: (options && options.page) ? options.page : 1,
      s: "DD"
    };
    if (options && options.license) {
      this._license = options.license;
      let _convertedLicenses: string = '';
      _convertedLicenses += this.licenseTypeToDuckduckgoParam(options.license);
      _convertedLicenses = _convertedLicenses.slice(0, _convertedLicenses.length);
      _queryObject.l = _convertedLicenses;
    }

    // step 1
    const url1 = ExternalImageSearchApis['duckduckgo'].url + 'start?' + this._http.convertObjectToQueryString(_queryObject);
    const result1: IDuckduckgoStep1Result = await this._http.get(url1);

    // step 2
    const url2 = ExternalImageSearchApis['duckduckgo'].url + result1.search_id + '/status'
    let result2: IDuckduckgoStep2Result = {
      status: 0
    }

    while (result2.status < 100.0) {
      result2 = await this._http.get(url2)
      await new Promise(f => setTimeout(f, 1000)); // sleep 1 second
    }

    // step 3
    const url3 = ExternalImageSearchApis['duckduckgo'].url + result1.search_id + '?' + this._http.convertObjectToQueryString(_queryObject);
    const result: IDuckduckgoImageSearchResult = await this._http.get(url3);
    return result;
  }

    /**
   * Convert the results into the internally used format
   * @param imageItems
   */
    public convertToImageWrapper(imageItems: IDuckduckgoPublicSearchImageItem[]): IImageWrapper[] {
      const results: IImageWrapper[] = [];
      for (const imageItem of imageItems) {
        results.push({
          externalId: null,
          base64: null,
          license: this.licenseStringToType(imageItem.license),
          licenseVersion: imageItem.license_version,
          licenseUrl: imageItem.license_url,
          description: null,
          title: imageItem.title,
          source: imageItem.thumbnail_url,//.url,
          thumbnailSource: imageItem.thumbnail_url,
          author: imageItem.author, // TODO Add auther url?
          dimension: {
            height: imageItem.height,
            width: imageItem.width,
            ratio: imageItem.width / imageItem.height
          }
        });
      }
      return results;
    }

      /**
   * Convert to standardized results
   * @param results The results of the query
   * @param currentPage The result's current page
   * @param licenses The result's licenses
   */
  public async convertToStandardizedApiResults(imageSearchResults: IDuckduckgoImageSearchResult, currentPage: number, licenses?: Licenses[]): Promise<IStandardizedImageSearchResults> {
    return {
      pageCount: 1, // Number(imageSearchResults..photos.pages),
      resultCount: Number(imageSearchResults.count),
      pageCurrent: currentPage,
      pageSize: Number(imageSearchResults.count),
      license: licenses,
      targetApi: 'duckduckgo',
      results: await this.convertToImageWrapper(imageSearchResults.results)
    };
  }
  
  /**
   * Convert a license by name and version to the proper enum
   * @param licenseString
   */
    public licenseStringToType(licenseString: string): Licenses {
      switch (licenseString) {
        case 'cc0':
          return Licenses.CC_ZERO;
        case 'by':
          return Licenses.CC_BY;
        default:
          return Licenses.UNKNOWN;
      }
    }

  /**
   * Convert the license to an id such that flickr understand the license
   * @param license The license to be converted to an id
   */
    public licenseTypeToDuckduckgoParam(license: Licenses): string {
    switch (license) {
      case Licenses.CC_BY: {
        return "CC_BY";
      }
      case Licenses.CC_ZERO: {
        return "public";
      }
      default: {
        throw new Error('License not supported by DuckduckGo');
      }
    }
  }
}